import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "../../../../node_modules/mapbox-gl/dist/mapbox-gl.css";
import zip_bounds from "../../../data/boston_zip.json";
import flip_by_zip from "../../../data/flip_by_zip.json";
import { Feature } from "geojson";
import { scaleLinear } from "d3";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

interface SpecMapProps {
  sliderVal: number;
}

const SpecMap: React.FC<SpecMapProps> = ({ sliderVal }) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const colorScale = scaleLinear<string>()
    .domain([0, 0.2])
    .range(["lime", "white"]); // output colors

  // Renders the map on mount
  useEffect(() => {
    // Load the mapbox map
    mapRef.current = new mapboxgl.Map({
      container: "spec-map",
      style: "mapbox://styles/mapbox/dark-v11",
      center: [-71.057, 42.312],
      zoom: 10,
    });
    mapRef.current.on("load", () => {
      const plotted_zip_codes = new Set<string>();
      zip_bounds.features.forEach((feature) => {
        // Extract the zip code from the feature
        const zip = feature.properties.ZIP5.replace(/^0+/, "");
        // Ensure there are no duplicate layers
        if (plotted_zip_codes.has(zip) || !flip_by_zip.hasOwnProperty(zip)) {
          return;
        }
        plotted_zip_codes.add(zip);
        // Calculate the flip rate for that zip code
        const zipData = flip_by_zip[zip as keyof typeof flip_by_zip];
        const flipRate = zipData[sliderVal.toString() as keyof typeof zipData];
        // Draw the polygon
        mapRef.current!.addLayer({
          id: `${zip}-poly`,
          type: "fill",
          source: {
            type: "geojson",
            data: feature as Feature,
          },
          paint: {
            "fill-color": colorScale(flipRate),
            "fill-opacity": 0.7,
          },
        });
        // Draw a border around the polygon
        mapRef.current!.addLayer({
          id: `${zip}-border`,
          type: "line",
          source: {
            type: "geojson",
            data: feature as Feature,
          },
          paint: {
            "line-color": "black",
            "line-width": 0.5,
            "line-opacity": 0.5,
          },
        });
        // Put the label on top
        mapRef.current!.addLayer({
          id: `${zip}-label`,
          type: "symbol",
          source: {
            type: "geojson",
            data: feature as Feature,
          },
          layout: {
            "text-field": `${Math.round(flipRate * 100)}%`,
            "text-size": 12,
          },
        });
      });
    });

    // Disable scroll zoom by default.
    // Enable scroll zoom when clicking on the map, and disable it again
    // when clicking outside the map.
    mapRef.current.scrollZoom.disable();
    const handleClick = (event: MouseEvent) => {
      const mapContainer = document.getElementById("spec-map");
      if (mapContainer && !mapContainer.contains(event.target as Node)) {
        mapRef.current?.scrollZoom.disable();
      } else {
        mapRef.current?.scrollZoom.enable();
      }
    };
    document.addEventListener("click", handleClick);

    // On cleanup, remove the map
    return () => {
      mapRef.current!.remove();
      document.removeEventListener("click", handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {
      const plotted_zip_codes = new Set<string>();
      zip_bounds.features.forEach((feature) => {
        // Extract the zip code from the feature
        const zip = feature.properties.ZIP5.replace(/^0+/, "");
        // Ensure there are no duplicate layers
        if (plotted_zip_codes.has(zip) || !flip_by_zip.hasOwnProperty(zip)) {
          return;
        }
        plotted_zip_codes.add(zip);
        // Calculate the flip rate for that zip code
        const zipData = flip_by_zip[zip as keyof typeof flip_by_zip];
        const flipRate = zipData[sliderVal.toString() as keyof typeof zipData];
        mapRef.current?.setPaintProperty(
          `${zip}-poly`,
          "fill-color",
          colorScale(flipRate)
        );
        mapRef.current?.setLayoutProperty(
          `${zip}-label`,
          "text-field",
          `${Math.round(flipRate * 100)}%`
        );
      });
      console.log(plotted_zip_codes);
    }
  }, [colorScale, sliderVal]);

  return <div id="spec-map" />;
};

export default SpecMap;
